<template>
<b-tab lazy title-item-class="mr-1" :title-link-class="{'bg-danger': hasErrors, 'bg-warning': isDirty}">
	<template slot="title">
		<i v-if="isDirty || hasErrors" class="fa-solid fa-circle-exclamation" :class="{ 'fa-circle-exclamation text-white': hasErrors, 'fa-cloud-arrow-up text-black': isDirty}"></i> {{ title }}
	</template>
	<validation-observer ref="observer" v-slot="{ passes }">
	<b-form @submit.prevent="passes(onFormSubmit)" novalidate>
        <b-overlay 
        :show="$store.getters['loading/isLoading']('insurance')"
        rounded="sm"
        variant="light"
        :opacity="0.80"
        blur="2px">
		<b-form-group :disabled="isLocked">
			<b-row>
				<b-col cols="12" md="6">
                    <validation-provider vid="number" v-slot="{ errors }">
                    <b-form-group
                        :invalid-feedback="errors[0]"
                        :state="errors[0] ? false : null">
	                    <b-form-input id="number" name="number" placeholder="* Number" v-model="form.number"/>
	                </b-form-group>
	                </validation-provider>
	                <validation-provider vid="effective_date" v-slot="{ errors }">
	                <b-form-group
                        :invalid-feedback="errors[0]"
                        :state="errors[0] ? false : null">
                		<b-form-input id="effective_date" placeholder="Effective date (DD-MM-YYYY)" v-model="form.effective_date" v-dateformat autocomplete="off"></b-form-input>
	                </b-form-group>
	                </validation-provider>
	                <validation-provider vid="expiry_date" v-slot="{ errors }">
	                <b-form-group
                        :invalid-feedback="errors[0]"
                        :state="errors[0] ? false : null">
                		<b-form-input id="expiry_date" placeholder="* Expiry date (DD-MM-YYYY)" v-model="form.expiry_date" v-dateformat autocomplete="off"></b-form-input>
	                </b-form-group>
	                </validation-provider>
				</b-col>
				<b-col cols="12" md="6">
					<validation-provider vid="company_id" v-slot="{ errors }">
	                <b-form-group
                        :invalid-feedback="errors[0]"
                        :state="errors[0] ? false : null">
	                    <b-form-select id="company_id" name="company_id" plain v-model="form.company_id" @change="onCompanyChange">
	                        <b-form-select-option :value="null">* Select insurance company</b-form-select-option>
	                        <b-form-select-option v-for="company in insurance_companies" :key="company.id" :value="company.id">{{ company.name }}
	                        </b-form-select-option>
	                    </b-form-select>
	                </b-form-group>
	                </validation-provider>
	                <validation-provider vid="type_id" v-slot="{ errors }">
	                <b-form-group
                        :invalid-feedback="errors[0]"
                        :state="errors[0] ? false : null">
	                    <b-form-select id="type_id" name="type_id" plain v-model="form.type_id">
	                        <b-form-select-option :value="null">* Select insurance type</b-form-select-option>
	                        <b-form-select-option v-for="type in filteredTypes" :key="type.id" :value="type.id">{{ type.name }}
	                        </b-form-select-option>
	                    </b-form-select>
	                </b-form-group>
	                </validation-provider>
				</b-col>
			</b-row>
		</b-form-group>
			
		<!-- Insurance Provider Verification Status -->
		<!-- Temporarily disabled - using test API, not production -->
		<b-row v-if="false" class="mt-3">
			<b-col cols="12">
				<b-card border-variant="light" class="mb-0">
					<div class="d-flex justify-content-between align-items-center">
						<div>
							<h6 class="mb-1">
								<i class="fa-solid fa-shield-check mr-1"></i> Insurance Provider Verification
								<b-badge v-if="insuranceStatus === 'valid'" variant="success" class="ml-2">
									<i class="fa-solid fa-check"></i> Valid
								</b-badge>
								<b-badge v-else-if="insuranceStatus === 'invalid'" variant="danger" class="ml-2">
									<i class="fa-solid fa-times"></i> {{ isExpired ? 'Expired' : 'Invalid' }}
								</b-badge>
								<b-badge v-else variant="secondary" class="ml-2">
									<i class="fa-solid fa-question"></i> Not Checked
								</b-badge>
							</h6>
						<small v-if="insurance?.last_check" class="text-muted d-block">
							Last checked: {{ insurance.last_check }}
						</small>
							<small v-else class="text-muted d-block">
								Never verified with Insurance Provider
							</small>
						<small v-if="isExpired" class="text-danger d-block mt-1">
							<i class="fa-solid fa-calendar-xmark"></i> 
							Insurance expired on {{ insurance.expiry_date_formatted }}
						</small>
					<small v-else-if="verificationChanges" class="text-warning d-block mt-1">
							<i class="fa-solid fa-exclamation-triangle"></i> 
							{{ verificationChanges }}
						</small>
						
						<!-- Display API Data -->
						<div v-if="apiData" class="mt-2 p-2 bg-light rounded">
							<small class="d-block font-weight-bold mb-1">
								<i class="fa-solid fa-info-circle"></i> Provider Information:
							</small>
							<small v-if="apiData.text_on_card" class="d-block">
								<strong>Insurance Type:</strong> {{ apiData.text_on_card }}
							</small>
							<small v-if="apiData.name" class="d-block">
								<strong>Name:</strong> {{ apiData.name }}
							</small>
							<small v-if="apiData.id_number" class="d-block">
								<strong>ID:</strong> {{ apiData.id_number }}
							</small>
							<small v-if="apiData.birth_date" class="d-block">
								<strong>Birth Date:</strong> {{ apiData.birth_date }}
							</small>
							<small v-if="apiData.gender" class="d-block">
								<strong>Gender:</strong> {{ apiData.gender }}
							</small>
						</div>
					</div>
						<div>
						<b-button 
							type="button"
							size="sm" 
							:variant="isSZFInsurance ? 'outline-primary' : 'outline-secondary'"
							@click="handleVerifyInsurance"
							:disabled="isVerifying || !insurance?.id || !isSZFInsurance"
							v-b-tooltip.hover="!isSZFInsurance ? 'Only SZF verification is currently available' : ''">
							<b-spinner small v-if="isVerifying"></b-spinner>
							<i v-else class="fa-solid fa-sync"></i>
							{{ isVerifying ? 'Verifying...' : 'Re-sync' }}
						</b-button>
						</div>
					</div>
				</b-card>
			</b-col>
		</b-row>
	</b-overlay>
    <template v-if="checkUserPermission('insurance', 'can_change')">
        <b-button class="mr-1" :disabled="isLocked" type="submit" variant="success"><i class="fa-regular fa-circle-dot"></i> Submit</b-button>
        <b-button v-if="insurance && insurance.id" class="mr-1" :disabled="isLocked" type="button" variant="danger" @click="deleteInsurance"><i class="fa-solid fa-trash"></i> Delete</b-button>
        <b-button v-if="isLocked" type="button" variant="warning" @click="isLocked = false"><i class="fa-solid fa-unlock"></i> Unlock</b-button>
        <b-button v-if="!isLocked && !isDirty && !hasErrors" type="button" variant="outline-primary" @click="isLocked = true"><i class="fa-solid fa fa-lock"></i> Lock</b-button>
        <b-button v-if="isDirty || hasErrors" type="button" variant="danger" @click="reset"><i class="fa-solid fa-ban"></i> Reset</b-button>
    </template>
	</b-form>
	</validation-observer>
</b-tab>
</template>
<script>
import { mapState, mapGetters, mapActions } from "vuex"
import TabExtension from "../TabExtension"

export default TabExtension.extend({
	name: "primary-insurance",
    props: {
        is_primary: {
            type: Boolean,
            default: false
        },
        title: {
            type: String
        },
        insurance: {
            type: Object,
        }
    },

	data() {
		return {
			isVerifying: false,
			verificationChanges: null,
			apiData: null,
			form: new Form(this.blankFormShape())
		}
	},
	computed: {
		...mapState("catalogue", {
            insurance_companies: (state) => state.insurance_companies,
            insurance_types: (state) => state.insurance_types
		}),
		filteredTypes() {
		    if (!this.form.company_id) {
		        return []
		    }

		    const company = this.insurance_companies.find(company => company.id === this.form.company_id)
		    if (!company) {
		        return []
		    }

		    const types = this.insurance_types.filter(item => item.parent === company.code)
		    return types.length > 0 ? types : this.insurance_types.filter(item => item.parent === null)
		},
		insuranceStatus() {
			if (this.insurance?.is_valid === true) return 'valid';
			if (this.insurance?.is_valid === false) return 'invalid';
			return 'unknown';
		},
		isExpired() {
			if (!this.insurance?.expiry_date) return false;
			const expiry = new Date(this.insurance.expiry_date);
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			return expiry < today;
		},
		isSZFInsurance() {
			if (!this.insurance?.company?.name) return false;
			return this.insurance.company.name.toLowerCase().includes('szf');
		}
	},
	watch: {
		insurance: {
			immediate: true,
			handler(val) {
                if (val) {
                    this.form.withData(val)
                } else {
                    this.form.withData(this.blankFormShape())
                    this.apiData = null
                    this.verificationChanges = null
                    this.$nextTick(() => {
                        this.$refs.observer?.reset()
                        this.hasErrors = false
                        this.validated()
                    })
                }
			}
		},
        form: {
            deep: true,
            handler(val) {
                this.isDirty = this.form.dirty()

                if (this.isDirty) {
                    this.dirty()
                }else{
                    this.validated()
                }
            }
        }
	},
	methods: {
        ...mapActions("catalogue", ["getCatalogueItem", ]),
		...mapActions("insurances", ["createPatientInsurance", "updatePatientInsurance", "deletePatientInsurance", "verifyPatientInsurances",	"getInsurancesForCurrentPatient"]),
		blankFormShape() {
			return {
				is_primary: this.is_primary,
				is_active: false,
				company_id: null,
				type_id: null,
				number: "",
				effective_date: null,
				expiry_date: null,
				is_valid: null,
				last_check: null,
				consistency_token: null
			}
		},
		onCompanyChange() {
			this.form.populate({type_id: null})
		},
		async handleVerifyInsurance() {
			if (!this.insurance?.id) {
				this.$bvToast.toast('Please save the insurance first before verifying', {
					title: 'Cannot Verify',
					variant: 'warning',
					solid: true
				});
				return;
			}
			
		this.isVerifying = true;
		this.verificationChanges = null;
		this.apiData = null;
		
		try {
			const result = await this.verifyPatientInsurances({
				insuranceId: this.insurance.id
			});
			
			await this.getInsurancesForCurrentPatient();
			
			if (result.api_data) {
				this.apiData = result.api_data;
			}				if (result.status === 'error') {
					this.$bvToast.toast(result.message || result.error || 'Verification failed', {
						title: 'Verification Failed',
						variant: 'danger',
						solid: true,
						autoHideDelay: 8000
					});
					return;
				}
				
			const changes = [];
			if (result.changes?.expiry_date) {
				changes.push(`Expiry: ${result.changes.expiry_date.old || 'none'} → ${result.changes.expiry_date.new}`);
			}
			if (result.changes?.effective_date) {
				changes.push(`Effective: ${result.changes.effective_date.old || 'none'} → ${result.changes.effective_date.new}`);
			}				let message = 'Insurance verified successfully';
				if (changes.length > 0) {
					message = `Insurance updated:\n${changes.join('\n')}`;
				}
				
				this.$bvToast.toast(message, {
					title: result.valid ? (result.expired ? 'Insurance Expired' : 'Insurance Valid') : 'Insurance Invalid',
					variant: result.valid && !result.expired ? 'success' : 'warning',
					solid: true,
					autoHideDelay: 5000
				});
				
			} catch (error) {
				const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to verify insurance';
				this.$bvToast.toast(errorMessage, {
					title: 'Verification Failed',
					variant: 'danger',
					solid: true,
					autoHideDelay: 8000
				});
			} finally {
				this.isVerifying = false;
			}
		},
		async triggerVerification(insuranceId) {
			try {
				const result = await this.verifyPatientInsurances({
					insuranceId: insuranceId
				});
				
				await this.getInsurancesForCurrentPatient();
				
				if (result.api_data) {
					this.apiData = result.api_data;
				}
			} catch (error) {
				console.error('Background verification failed:', error);
			}
		},
		async create() {
			try {
				const response = await this.createPatientInsurance(this.form.data());
				const createdInsurance = this.$store.getters['insurances/getPatientInsurances']().find(
					ins => ins.is_primary === this.is_primary
				);
				
				this.reset();
				this.notify(`${this.title} insurance created`, `${this.title} insurance was created successfully`);
				
				// Temporarily commented out - using test API, not production
				// if (createdInsurance?.id) {
				// 	this.triggerVerification(createdInsurance.id);
				// }
			} catch (errors) {
				if (errors.response) {
					this.$refs.observer?.setErrors(errors.response.data);
					this.hasErrors = true;
				}
			}
		},
		async update() {
			try {
				await this.updatePatientInsurance({id: this.insurance?.id, insurance: this.form.data() });
				this.reset();
				this.notify(`${this.title} insurance updated`, `${this.title} insurance was updated successfully`);
				
				// Temporarily commented out - using test API, not production
				// if (this.insurance?.id) {
				// 	this.triggerVerification(this.insurance.id);
				// }
			} catch (errors) {
				if (errors.response) {
					this.$refs.observer?.setErrors(errors.response.data);
					this.hasErrors = true;
				}
			}
		},
		onFormSubmit() {
			if (this.insurance) {
				this.update()
			}else{
				this.create()
			}
		},
		async deleteInsurance() {
			if (!this.insurance?.id) return
			const confirmed = await this.$bvModal.msgBoxConfirm(
				`Are you sure you want to delete the ${this.title.toLowerCase()} insurance? This cannot be undone.`, {
					title: `Delete ${this.title} insurance`,
					okVariant: 'danger',
					okTitle: 'Delete',
					cancelTitle: 'Cancel',
					centered: true
				}
			)
			if (!confirmed) return
			try {
				await this.deletePatientInsurance(this.insurance.id)
				this.reset()
				this.notify(`${this.title} insurance deleted`, `${this.title} insurance was deleted successfully`)
			} catch (error) {
				const message = error.response?.data?.message || error.response?.data?.detail || error.message || 'Failed to delete insurance'
				this.$bvToast.toast(message, {
					title: 'Delete Failed',
					variant: 'danger',
					solid: true,
					autoHideDelay: 6000
				})
			}
		},
        reset() {
            this.form.reset()

            this.$nextTick(() => {
            	this.$refs.observer?.reset()
                this.hasErrors = false
                this.validated()

            })
        }
    },
    async mounted() {
        await this.getCatalogueItem('insurance_companies')
        await this.getCatalogueItem('insurance_types')
    }
})
</script>